/** A trigger button and the small panel it opens: the one popover the table's tools share.
 *
 * `kind="menu"` is a command menu (`role="menu"`): focus lands on the first item, the arrow keys, Home
 * and End move between items, and Tab leaves. `kind="panel"` holds anything else - the column
 * chooser's tick-boxes, Export's button - and is a non-modal dialog (the trigger says
 * `aria-haspopup="dialog"`, so that is what opens); focus lands on the panel, and Tab and Shift+Tab
 * cycle through its controls and stay inside while it is open (G-P10-07: Tab used to close it before
 * the second control could be reached). Either way Escape closes and hands focus back to the trigger,
 * and a press outside closes without moving focus.
 *
 * It can be **controlled** (`open`, `onOpenChange`) because the column menu is also opened by a key on
 * the header (Alt+Down), which the header owns.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, ReactNode } from "react";

import styles from "./tablePower.module.css";
import { menuItems, onMenuKeyDown, useDismiss } from "./menuKeys";

export interface PopMenuProps {
  /** The trigger's accessible name. */
  label: string;
  /** What the trigger shows. */
  trigger: ReactNode;
  children: (close: (returnFocus?: boolean) => void) => ReactNode;
  kind?: "menu" | "panel";
  align?: "left" | "right";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerClassName?: string;
  testId?: string;
  disabled?: boolean;
  /** Marks the trigger as a column menu's chevron, which the grid reveals on header hover and focus. */
  columnMenu?: boolean;
  /** Draw the panel at the trigger's place on the screen (`position: fixed`) instead of inside its
   * parent, so a cell, a card or a scroll area that clips what overflows does not cut it. It closes when
   * the page scrolls or the window resizes, since it would no longer sit under its trigger. For a menu in a
   * table row. */
  floating?: boolean;
}

/** Where a floating panel goes: under the trigger, its left or right edge on the trigger's, and kept on screen. */
function floatAt(trigger: HTMLElement, align: "left" | "right"): CSSProperties {
  const box = trigger.getBoundingClientRect();
  const top = box.bottom + 6;
  return align === "right"
    ? { position: "fixed", top, right: Math.max(8, window.innerWidth - box.right), left: "auto" }
    : { position: "fixed", top, left: Math.max(8, box.left) };
}

const TABBABLE =
  "button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),a[href],[tabindex]:not([tabindex='-1'])";

/** Tab and Shift+Tab cycle through a panel's controls and never leave it. Between two controls the
 * browser moves focus itself; only the ends (and the panel, which Shift+Tab would step behind) are ours. */
function keepTabInside(event: KeyboardEvent<HTMLDivElement>, root: HTMLDivElement | null): void {
  if (!root) return;
  const controls = [...root.querySelectorAll<HTMLElement>(TABBABLE)];
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const at = document.activeElement;
  if (event.shiftKey && (at === first || at === root)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && at === last) {
    event.preventDefault();
    first.focus();
  }
}

export function PopMenu({
  label,
  trigger,
  children,
  kind = "menu",
  align = "left",
  open: controlled,
  onOpenChange,
  triggerClassName,
  testId,
  disabled = false,
  columnMenu = false,
  floating = false,
}: PopMenuProps) {
  const [inner, setInner] = useState(false);
  const [placed, setPlaced] = useState<CSSProperties | undefined>(undefined);
  const open = controlled ?? inner;
  const setOpen = useCallback(
    (next: boolean) => {
      if (onOpenChange) onOpenChange(next);
      else setInner(next);
    },
    [onOpenChange],
  );
  const ownTrigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const triggerId = `${id}-trigger`;
  // Focus goes back by id rather than by a ref the render would have to read.
  const close = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      if (returnFocus) document.getElementById(triggerId)?.focus();
    },
    [setOpen, triggerId],
  );
  useDismiss(open, () => setOpen(false), ownTrigger, panel);

  // A floating panel is placed from the trigger's box when it opens, and closed when the page moves.
  useLayoutEffect(() => {
    if (!floating || !open || !ownTrigger.current) return;
    setPlaced(floatAt(ownTrigger.current, align));
    const away = () => setOpen(false);
    window.addEventListener("resize", away);
    window.addEventListener("scroll", away, true);
    return () => {
      window.removeEventListener("resize", away);
      window.removeEventListener("scroll", away, true);
    };
  }, [floating, open, align, setOpen]);

  useEffect(() => {
    if (!open) return;
    if (kind === "menu") menuItems(panel.current)[0]?.focus();
    else panel.current?.focus();
  }, [open, kind]);

  const onPanelKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab") {
      if (kind === "panel") keepTabInside(event, panel.current);
      else setOpen(false);
    } else if (kind === "menu") {
      onMenuKeyDown(event, panel.current);
    }
  };

  return (
    <span className={styles.pop}>
      <button
        ref={ownTrigger}
        id={triggerId}
        type="button"
        className={triggerClassName ?? styles.tool}
        aria-label={label}
        aria-haspopup={kind === "menu" ? "menu" : "dialog"}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        disabled={disabled}
        data-testid={testId}
        data-col-menu={columnMenu ? "" : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open && !event.altKey) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {trigger}
      </button>
      {open ? (
        <div
          ref={panel}
          id={id}
          role={kind === "menu" ? "menu" : "dialog"}
          aria-label={label}
          tabIndex={-1}
          className={`${styles.panel} ${align === "right" ? styles.panelRight : ""}`}
          style={floating ? (placed ?? { position: "fixed", top: 0, left: 0 }) : undefined}
          onKeyDown={onPanelKey}
        >
          {children(close)}
        </div>
      ) : null}
    </span>
  );
}
